<template>
  <v-container fluid>
    <v-row justify="start">
      <v-col>
        <h1>Generate Short Form Lead</h1>
        <p>Fill out the form sections below to generate a new test lead.</p>
      </v-col>
      <v-col
        v-if="!dataLoading && !showError"
        class="d-flex align-end justify-end"
      >
        <v-btn
          id="submit_lead_btn"
          color="primary"
          class="submit-btn mb-4"
          :loading="formLoading"
          @click="submitLead"
        >
          Submit Short Form Lead
        </v-btn>
      </v-col>
    </v-row>

    <template
      v-if="dataLoading"
    >
      <p>Loading data</p>
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      />
    </template>

    <v-row
      v-else
    >
      <v-col
        v-if="!showError"
      >
        <v-stepper
          v-model="step"
          non-linear
        >
          <v-stepper-header>
            <v-stepper-item
              editable
              :value="1"
              title="Lead Data"
            />
          </v-stepper-header>

          <v-stepper-window>
            <v-stepper-window-item
              :value="1"
            >
              <ShortFormLeadDataForm
                :lead-data="leadData"
                :campaigns="campaigns"
                :lead-providers="leadProviders"
              />
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-col>

      <v-col
        v-if="showError"
        cols="12"
      >
        <v-alert
          type="error"
          dark
          border="start"
        >
          Failed to retrieve data needed to create lead.
          <ul>
            <li
              v-for="(msg, index) in errorMessages"
              :key="index"
            >
              {{ msg }}
            </li>
          </ul>
        </v-alert>
      </v-col>

      <v-dialog
        v-model="showModal"
      >
        <v-card
          flat
          class="pa-2"
        >
          <v-card-title>Result</v-card-title>
          <v-card-text>
            <v-alert
              v-show="formError"
              type="error"
              dark
              border="start"
            >
              {{ formError }}
            </v-alert>

            <vue-json-pretty
              :data="apiResponse"
              :show-line="false"
              :show-double-quotes="false"
              virtual
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

import { mapActions, mapGetters } from 'vuex';
import stateList from '@/data/states.json';
import ShortFormLeadDataForm from '@/views/TestSuite/ShortFormLead/ShortFormLeadDataForm.vue';

export default {
  components: {
    ShortFormLeadDataForm,
    VueJsonPretty,
  },
  data() {
    return {
      dataLoading: true,
      showError: false,
      errorMessages: [],
      customers: [],
      campaigns: [],
      leadProviders: [],
      usStates: [],
      lastpaydate: '',
      nextpaydate: '',
      secondpaydate: '',
      step: 1,
      formLoading: false,
      showModal: false,
      apiResponse: {},
      formError: '',
      loanStatuses: [],
      loanSubStatuses: [],
    };
  },
  computed: {
    isReturnCampaign() {
      const campaign = this.campaigns.find((camp) => camp.value === this.leadData.campaign);
      if (campaign && campaign.text.includes('Return')) {
        if (this.customers.length > 0) {
          this.updateLeadData({
            return_customer: 1,
            firstname: this.customers[0].firstname,
            lastname: this.customers[0].lastname,
            email: this.customers[0].email,
            customer_guid: this.customers[0].value,
            homephone: this.customers[0].homephone,
          });
        }
        return true;
      }
      this.updateLeadData({
        return_customer: 0,
        customer_guid: null,
      });
      return false;
    },
    ...mapGetters('testSuite', ['leadData']),
  },
  async created() {
    this.usStates = stateList.map((state) => state.id);
    this.$axios
      .get('/api/v1/waterfall/test')
      .then((res) => {
        const { data } = res;
        this.customers = data.customers.map((customer) => ({
          value: customer.guid,
          text: `${customer.firstname} ${customer.lastname}`,
          firstname: customer.firstname,
          lastname: customer.lastname,
          email: customer.email,
          homephone: customer.homephone,
        }));

        this.campaigns = data.campaigns.map((campaign) => ({
          value: campaign.id,
          text: `${campaign.id} (${campaign.campaign_name} - ${campaign.product_type})`,
        }));

        if (this.campaigns.length === 0) {
          this.errorMessages.push('No Campaigns found.');
        } else {
          this.leadData.campaign = this.campaigns[0].value;
        }

        this.leadProviders = data.lead_providers.map((provider) => ({
          value: provider.api_key,
          text: `${provider.api_key} (${provider.category})`,
        }));

        if (this.leadProviders.length === 0) {
          this.errorMessages.push('No Lead Providers found.');
        } else {
          this.leadData.api_key = this.leadProviders[0].value;
        }

        if (this.errorMessages.length > 0) {
          this.showError = true;
        }

        this.dataLoading = false;

        this.leadData.lastpaydate = data.last_pay_date;
        this.leadData.nextpaydate = data.next_pay_date;
        this.leadData.secondpaydate = data.second_pay_date;

        this.leadData.address = data.address;
        this.leadData.homephone = data.homephone;
        this.leadData.ssn = data.ssn;
        this.leadData.accountnumber = data.accountnumber;

        // populate loan statuses
        this.loanStatuses = data.loan_statuses;
        this.loanSubStatuses = data.loan_sub_statuses;

        // update vuex store
        this.setLeadData(this.leadData);
      });
  },
  methods: {
    ...mapActions('testSuite', ['setLeadData', 'updateLeadData']),
    ...mapActions('notification', ['setNotification']),

    getDaysByFrequency(frequency) {
      const daysByFrequency = [
        { frequency: 'W', days: 7 },
        { frequency: 'B', days: 14 },
        { frequency: 'S', days: 15 },
        { frequency: 'M', days: 30 },
      ];

      return !frequency ? 7 : daysByFrequency.find((freq) => freq.frequency === frequency).days;
    },

    calculateDatesForSemimonthly(nextDate) {
      let adjustedNextDate; let secondPayDate; let
        lastPayDate;

      if (nextDate.getDate() < 15) {
        adjustedNextDate = new Date(nextDate.getFullYear(), nextDate.getMonth(), 15);
        secondPayDate = new Date(
          adjustedNextDate.getFullYear(),
          adjustedNextDate.getMonth() + 1,
          1,
        );
        lastPayDate = new Date(
          adjustedNextDate.getFullYear(),
          adjustedNextDate.getMonth(),
          1,
        );
      } else {
        adjustedNextDate = new Date(
          nextDate.getFullYear(),
          nextDate.getMonth() + 1,
          1,
        );
        secondPayDate = new Date(
          adjustedNextDate.getFullYear(),
          adjustedNextDate.getMonth(),
          15,
        );
        lastPayDate = new Date(
          adjustedNextDate.getFullYear(),
          adjustedNextDate.getMonth() - 1,
          15,
        );
      }

      this.updateLeadData({
        nextpaydate: this.formatDate(adjustedNextDate),
        secondpaydate: this.formatDate(secondPayDate),
        lastpaydate: this.formatDate(lastPayDate),
      });
    },

    calculateDatesForOtherFrequencies(frequency, nextDate) {
      const daysNeeded = this.getDaysByFrequency(frequency);
      const adjustedNextDate = nextDate;
      const secondPayDate = this.addDays(adjustedNextDate, daysNeeded);
      const lastPayDate = this.addDays(adjustedNextDate, daysNeeded * -1);

      this.updateLeadData({
        secondpaydate: this.formatDate(secondPayDate),
        lastpaydate: this.formatDate(lastPayDate),
      });
    },

    calculateDatesForMonthly(nextDate) {
      const adjustedNextDate = nextDate;
      const secondPayDate = new Date(
        adjustedNextDate.getFullYear(),
        adjustedNextDate.getMonth() + 1,
        adjustedNextDate.getDate(),
      );
      const lastPayDate = new Date(
        adjustedNextDate.getFullYear(),
        adjustedNextDate.getMonth() - 1,
        adjustedNextDate.getDate(),
      );

      this.updateLeadData({
        secondpaydate: this.formatDate(secondPayDate),
        lastpaydate: this.formatDate(lastPayDate),
      });
    },

    updateDates(nextPayDate, frequency) {
      const nextDate = new Date(nextPayDate);

      switch (frequency) {
        case 'S':
          this.calculateDatesForSemimonthly(nextDate);
          break;
        case 'M':
          this.calculateDatesForMonthly(nextDate);
          break;
        default:
          this.calculateDatesForOtherFrequencies(frequency, nextDate);
      }
    },

    addDays(dateStr, days) {
      const date = new Date(dateStr);
      date.setDate(date.getDate() + days);

      return date;
    },

    formatDate(date) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero based
      const dd = String(date.getDate()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd}`;
    },

    handleChangedNextPayDate(payData) {
      this.updateLeadData({
        nextpaydate: payData.nextPayDate,
      });

      this.updateDates(this.leadData.nextpaydate, payData.payFrequency);
    },

    handleChangedFrequency(payFrequency) {
      this.updateDates(this.leadData.nextpaydate, payFrequency);
    },

    submitLead() {
      this.formLoading = true;
      this.formError = '';

      this.$axios
        .post('api/short-form', this.leadData)
        .then((res) => {
          this.apiResponse = res.data;
        })
        .catch((error) => {
          if (error.response && error.response.status === 422) {
            this.apiResponse = error.response.data;
            this.formError = 'Please fix the following errors';
          } else {
            this.setNotification({
              message: 'There was an error processing the lead, please try again',
              color: 'red',
            });
          }
        })
        .then(() => {
          this.showModal = true;
          this.formLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.submit-btn {
  background-color: #205e86 !important;
  color: white !important;
  font-weight: 700 !important;
}
</style>
